import {
    useCallback,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
} from "react";
import { isAxiosError } from "axios";
import {
    ChartEditorWireOperation,
    sendChartEditorOperation,
} from "@api/chartEditor.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ChartEditorTempIdMap,
    recordChartEditorCreatedIds,
    substituteChartEditorTempIds,
} from "./chartEditorTempIds";

const WRITE_FAILURE_MESSAGE = "Failed to update the chart of accounts";
const RECOVERY_TOAST_KEY = "chart-editor-recovery";
const RECOVERY_MESSAGE =
    "Could not reload the chart of accounts. Retry to keep editing.";

interface Params {
    isOpen: boolean;
    workspaceId: string | null;
    // Rejects when the chart could not be reloaded; the executor never remounts
    // onto a cache that predates a write it may have lost the answer to.
    refetchChart: () => Promise<void>;
    invalidateChart: () => void | Promise<void>;
}

export interface WorkspaceChartEditorExecutor {
    enqueue: (operation: ChartEditorWireOperation) => void;
    // Remount key for the editor content: a bump reseeds it from the server and
    // drops selection, row expansion and the temp-id map with it.
    contentKey: number;
    // The reopen is waiting on the previous session's write or cache refresh.
    isAwaitingPreviousSession: boolean;
    // The recovery refetch failed: the draft stays on screen but takes no more
    // work until Retry gets the server's chart back.
    isRecoveryPending: boolean;
    retryRecovery: () => void;
    isWriting: boolean;
    isResyncing: boolean;
    // Click paths await this so a same-turn name-cell enqueue lands first.
    whenIdle: () => Promise<void>;
    getSessionToken: () => number;
    isSessionCurrent: (token: number) => boolean;
    isSessionBlocked: () => boolean;
    // Keeps a merge POST / remove DELETE observable after the content unmounts.
    trackExternalWrite: (promise: Promise<unknown>) => void;
    resync: () => void;
}

// Strictly serial, optimistic, and owned above the content it drives: closing
// only unmounts the content, so a queue living there would die with the session
// it has to outlive.
export const useWorkspaceChartEditorExecutor = ({
    isOpen,
    workspaceId,
    refetchChart,
    invalidateChart,
}: Params): WorkspaceChartEditorExecutor => {
    const toaster = useToaster();
    const queueRef = useRef<ChartEditorWireOperation[]>([]);
    const tempIdsRef = useRef<ChartEditorTempIdMap>(new Map());
    const inFlightRef = useRef<Promise<void> | null>(null);
    const cacheRefreshRef = useRef<Promise<void> | null>(null);
    const externalWriteRef = useRef<Promise<void> | null>(null);
    const pendingExternalReseedRef = useRef(false);
    const isDrainingRef = useRef(false);
    const isRecoveryPendingRef = useRef(false);
    const isResyncingRef = useRef(false);
    const isWritingRef = useRef(false);
    const isAwaitingPreviousSessionRef = useRef(false);
    const sessionTokenRef = useRef(0);
    const wasOpenRef = useRef(false);
    const drainDoneRef = useRef<Promise<void> | null>(null);

    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const [contentKey, setContentKey] = useState(0);
    const [isRecoveryPending, setIsRecoveryPending] = useState(false);
    const [isResyncing, setIsResyncing] = useState(false);
    const [isWriting, setIsWriting] = useState(false);
    const [isAwaitingPreviousSession, setIsAwaitingPreviousSession] =
        useState(false);

    const setWriting = useCallback((value: boolean) => {
        isWritingRef.current = value;
        setIsWriting(value);
    }, []);

    const setAwaitingPreviousSession = useCallback((value: boolean) => {
        isAwaitingPreviousSessionRef.current = value;
        setIsAwaitingPreviousSession(value);
    }, []);

    const startSession = useCallback(() => {
        sessionTokenRef.current += 1;
        queueRef.current = [];
        tempIdsRef.current = new Map();
        setContentKey((key) => key + 1);
    }, []);

    const enterRecovery = useCallback(() => {
        isRecoveryPendingRef.current = true;
        setIsRecoveryPending(true);
        toaster.toast(RECOVERY_MESSAGE, RECOVERY_TOAST_KEY, {
            preventAutoClose: true,
        });
    }, [toaster]);

    const recoverFromServer = useCallback(async () => {
        const session = sessionTokenRef.current;
        isRecoveryPendingRef.current = true;
        setIsRecoveryPending(true);
        try {
            await refetchChart();
        } catch {
            if (session === sessionTokenRef.current) {
                enterRecovery();
            }
            return;
        }
        if (session !== sessionTokenRef.current) {
            return;
        }
        isRecoveryPendingRef.current = false;
        setIsRecoveryPending(false);
        toaster.dismiss(RECOVERY_TOAST_KEY);
        startSession();
    }, [enterRecovery, refetchChart, startSession, toaster]);

    const handleWriteFailure = useCallback(
        async (error: unknown) => {
            // The interceptor already cleared auth on a 401; nothing to say and
            // nothing to reload.
            if (isAxiosError(error) && error.response?.status === 401) {
                return;
            }
            isRecoveryPendingRef.current = true;
            setIsRecoveryPending(true);
            toaster.error(WRITE_FAILURE_MESSAGE);
            await recoverFromServer();
        },
        [recoverFromServer, toaster],
    );

    const drain = useCallback(async () => {
        if (isDrainingRef.current || workspaceId === null) {
            return;
        }
        isDrainingRef.current = true;
        setWriting(true);
        let resolveIdle: () => void = () => undefined;
        drainDoneRef.current = new Promise<void>((resolve) => {
            resolveIdle = resolve;
        });
        // A drain that ends while an external write is still tracked leaves
        // isWriting true; the external write's settle clears it.
        const clearWritingIfIdle = () => {
            if (externalWriteRef.current === null) {
                setWriting(false);
            }
        };
        while (queueRef.current.length > 0) {
            const session = sessionTokenRef.current;
            const queued = queueRef.current.shift();
            if (queued === undefined) {
                break;
            }
            const operation = substituteChartEditorTempIds(
                queued,
                tempIdsRef.current,
            );
            const request = sendChartEditorOperation({
                workspaceId,
                operation,
            });
            // A reopen waits on this, so it must never reject on its own.
            inFlightRef.current = request.then(
                () => undefined,
                () => undefined,
            );
            try {
                const accounts = await request;
                if (session === sessionTokenRef.current) {
                    recordChartEditorCreatedIds({
                        operation,
                        accounts,
                        tempIds: tempIdsRef.current,
                    });
                }
            } catch (error) {
                queueRef.current = [];
                inFlightRef.current = null;
                clearWritingIfIdle();
                const failure =
                    session === sessionTokenRef.current
                        ? handleWriteFailure(error)
                        : Promise.resolve();
                await failure.finally(() => {
                    isDrainingRef.current = false;
                    drainDoneRef.current = null;
                    clearWritingIfIdle();
                    resolveIdle();
                });
                return;
            }
        }
        inFlightRef.current = null;
        isDrainingRef.current = false;
        clearWritingIfIdle();
        drainDoneRef.current = null;
        resolveIdle();
    }, [handleWriteFailure, workspaceId, setWriting]);

    const enqueue = useCallback(
        (operation: ChartEditorWireOperation) => {
            if (isRecoveryPendingRef.current || isResyncingRef.current) {
                return;
            }
            queueRef.current.push(operation);
            void drain();
        },
        [drain],
    );

    const whenIdle = useCallback(async () => {
        const done = drainDoneRef.current;
        if (done !== null) {
            await done;
        }
    }, []);

    const getSessionToken = useCallback(() => sessionTokenRef.current, []);
    const isSessionCurrent = useCallback(
        (token: number) => sessionTokenRef.current === token,
        [],
    );
    const isSessionBlocked = useCallback(
        () => isRecoveryPendingRef.current || isResyncingRef.current,
        [],
    );

    const trackExternalWrite = useCallback(
        (promise: Promise<unknown>) => {
            // Neutralise so a rejection cannot throw out of the awaits that
            // guard reopen, and cannot poison a composite over two writes.
            const neutralised = promise.then(
                () => undefined,
                () => undefined,
            );
            const previous = externalWriteRef.current;
            const composite =
                previous === null
                    ? neutralised
                    : Promise.all([previous, neutralised]).then(
                          () => undefined,
                      );
            externalWriteRef.current = composite;
            setWriting(true);
            void composite.then(() => {
                // A later trackExternalWrite must not be cleared by this one
                // finishing.
                if (externalWriteRef.current !== composite) {
                    return;
                }
                externalWriteRef.current = null;
                if (!isDrainingRef.current) {
                    setWriting(false);
                }
                // The modal can unmount mid-request, so its onMerged / onRemoved
                // may never run: reseed on the next open regardless.
                if (!isOpenRef.current) {
                    pendingExternalReseedRef.current = true;
                }
            });
        },
        [setWriting],
    );

    const resync = useCallback(async () => {
        const session = sessionTokenRef.current;
        // Closed drawer: leave a reseed for the next open. Awaiting reopen:
        // openSession is already waiting on this same write and will seed.
        const shouldStopAtBoundary = (): boolean => {
            if (!isOpenRef.current) {
                pendingExternalReseedRef.current = true;
                return true;
            }
            return isAwaitingPreviousSessionRef.current;
        };
        if (shouldStopAtBoundary()) {
            return;
        }
        await whenIdle();
        if (shouldStopAtBoundary()) {
            return;
        }
        isResyncingRef.current = true;
        setIsResyncing(true);
        let didRefetch: boolean;
        try {
            await refetchChart();
            didRefetch = true;
        } catch {
            didRefetch = false;
        }
        // Close already cleared these; a stale finally would unpin a newer resync.
        if (session !== sessionTokenRef.current) {
            if (!isOpenRef.current) {
                pendingExternalReseedRef.current = true;
            }
            return;
        }
        isResyncingRef.current = false;
        setIsResyncing(false);
        if (!didRefetch) {
            // Not recoverFromServer (refetches again) or handleWriteFailure
            // (wrong toast copy).
            enterRecovery();
            return;
        }
        if (shouldStopAtBoundary()) {
            return;
        }
        startSession();
        toaster.dismiss(RECOVERY_TOAST_KEY);
    }, [whenIdle, refetchChart, startSession, toaster, enterRecovery]);

    const openSession = useCallback(async () => {
        const session = sessionTokenRef.current;
        const pendingWrite = inFlightRef.current;
        const pendingCache = cacheRefreshRef.current;
        const pendingExternal = externalWriteRef.current;
        const needsReseed = pendingExternalReseedRef.current;
        if (
            pendingWrite === null &&
            pendingCache === null &&
            pendingExternal === null &&
            !needsReseed
        ) {
            isRecoveryPendingRef.current = false;
            setIsRecoveryPending(false);
            // Stale slow path can leave the loader stuck otherwise.
            setAwaitingPreviousSession(false);
            startSession();
            return;
        }
        // The cache can predate that write, a close's invalidation can still be
        // in flight, and a tracked merge / remove may not have landed yet. Stay
        // on the loader and never mount a pre-success tree.
        setAwaitingPreviousSession(true);
        if (pendingWrite !== null) {
            await pendingWrite;
        }
        if (pendingExternal !== null) {
            await pendingExternal;
        }
        let isReloadFailed = false;
        try {
            if (pendingCache !== null) {
                await pendingCache;
            } else {
                await refetchChart();
            }
        } catch {
            isReloadFailed = true;
        }
        // Close already reset recovery and the loader.
        if (session !== sessionTokenRef.current) {
            return;
        }
        if (isReloadFailed) {
            enterRecovery();
        } else {
            isRecoveryPendingRef.current = false;
            setIsRecoveryPending(false);
            toaster.dismiss(RECOVERY_TOAST_KEY);
        }
        cacheRefreshRef.current = null;
        pendingExternalReseedRef.current = false;
        startSession();
        setAwaitingPreviousSession(false);
    }, [
        enterRecovery,
        refetchChart,
        startSession,
        toaster,
        setAwaitingPreviousSession,
    ]);

    const closeSession = useCallback(async () => {
        sessionTokenRef.current += 1;
        queueRef.current = [];
        tempIdsRef.current = new Map();
        isRecoveryPendingRef.current = false;
        setIsRecoveryPending(false);
        isResyncingRef.current = false;
        setIsResyncing(false);
        toaster.dismiss(RECOVERY_TOAST_KEY);
        const pending = inFlightRef.current;
        if (pending !== null) {
            await pending;
        }
        const pendingExternal = externalWriteRef.current;
        if (pendingExternal !== null) {
            await pendingExternal;
        }
        // The chart page under the drawer still holds the pre-edit cache, and
        // `refetchOnWindowFocus` is off there. Keep the promise so a reopen
        // can wait out a still-running invalidation.
        const refresh = invalidateChart();
        if (refresh !== undefined) {
            const pendingRefresh = Promise.resolve(refresh);
            cacheRefreshRef.current = pendingRefresh;
            void pendingRefresh.then(
                () => {
                    if (cacheRefreshRef.current === pendingRefresh) {
                        cacheRefreshRef.current = null;
                    }
                },
                () => undefined,
            );
        }
    }, [invalidateChart, toaster]);

    useEffect(() => {
        if (isOpen === wasOpenRef.current) {
            return;
        }
        wasOpenRef.current = isOpen;
        if (isOpen) {
            void openSession();
            return;
        }
        void closeSession();
    }, [closeSession, isOpen, openSession]);

    // Leaving the page unmounts the drawer; the tail goes, the in-flight write
    // still lands.
    // `dismiss` is stable; putting `toaster` in the deps would re-run
    // cleanup on every render under a per-call test mock and bump the
    // session token each time.
    const createUnmountCleanup = useEffectEvent(() => () => {
        sessionTokenRef.current += 1;
        queueRef.current = [];
        toaster.dismiss(RECOVERY_TOAST_KEY);
    });
    useEffect(() => createUnmountCleanup(), []);

    const retryRecovery = useCallback(() => {
        void recoverFromServer();
    }, [recoverFromServer]);

    return {
        enqueue,
        contentKey,
        isAwaitingPreviousSession,
        isRecoveryPending,
        retryRecovery,
        isWriting,
        isResyncing,
        whenIdle,
        getSessionToken,
        isSessionCurrent,
        isSessionBlocked,
        trackExternalWrite,
        resync,
    };
};
